const baseUrl = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiOptions {
  token?: string;
  signal?: AbortSignal;
}

export async function apiGet<T>(path: string, options: ApiOptions = {}): Promise<T> {
  return apiRequest<T>('GET', path, undefined, options);
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  options: ApiOptions = {},
): Promise<T> {
  return apiRequest<T>('POST', path, body, options);
}

export async function apiDelete<T>(
  path: string,
  body?: unknown,
  options: ApiOptions = {},
): Promise<T> {
  return apiRequest<T>('DELETE', path, body, options);
}

export async function apiPatch<T>(
  path: string,
  body: unknown,
  options: ApiOptions = {},
): Promise<T> {
  return apiRequest<T>('PATCH', path, body, options);
}

async function apiRequest<T>(
  method: string,
  path: string,
  body: unknown,
  { token, signal }: ApiOptions,
): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (token) headers['authorization'] = `Bearer ${token}`;
  const url = `${baseUrl}${path}`;
  const init: RequestInit = { method, headers };
  if (body !== undefined) init.body = JSON.stringify(body);
  if (signal) init.signal = signal;
  const res = await fetch(url, init);
  const text = await res.text();
  let payload: unknown = undefined;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }
  if (!res.ok) {
    const err = (payload ?? {}) as { error?: string; code?: string };
    throw new ApiError(res.status, err.error ?? `HTTP ${res.status}`, err.code);
  }
  return payload as T;
}
